// @ts-nocheck
import { isEmpty } from 'lodash';
import moment from 'moment-timezone';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { showNotification, useQueryFetch } from 'sonata-react-components';
import { BaseCurrencyEnum } from 'sonata-static-storage/lib/enum/baseCurrency.enum';

import {
  saveScheduleReports,
  storeNewReportTemplate,
  updateReportTemplate,
  updateScheduleReports
} from '@/modules/reporting/scheduleReport.resources';
import { getBaseCurrencyName } from '@/store/selectors/baseCurrency.selectors';
import { getSelectedCampaigns } from '@/store/selectors/reporting.selectors';
import { getCurrency } from '@/store/selectors/reportingAdvancedFilter.selectors';
import { getUserId } from '@/store/selectors/user.selectors';

import type { ScheduleReportInterface } from 'sonata-static-storage/lib/schemas/scheduleReport/scheduleReport.schema';
import type { ExportFileFormat, ScheduleConfig, TabOptions } from '../types';

type State = {
  cacheSeed: string;
  isUpdating: boolean;
  prevScheduleConfig?: ScheduleReportInterface;
  successCallback?: CallableFunction;
};

export function useScheduleReportRequest(
  name: string,
  tabsOptions: TabOptions[],
  type: ExportFileFormat,
  scheduleConfig: ScheduleConfig
) {
  const { formatMessage } = useIntl();
  const baseCurrency = useSelector(getBaseCurrencyName);
  const currency = useSelector(getCurrency);
  const campaigns = useSelector(getSelectedCampaigns);
  const campaignsIds = campaigns.map((campaign: any) => campaign.id);
  const userId = useSelector(getUserId);
  const tabs = type === 'csv' ? [tabsOptions[0]] : tabsOptions;

  const currencyOptions = {
    baseCurrency: baseCurrency as BaseCurrencyEnum,
    currency: currency as BaseCurrencyEnum
  };

  const [{ cacheSeed, isUpdating, prevScheduleConfig, successCallback }, setState] = useState<State>({
    cacheSeed: new Date().toISOString(),
    isUpdating: false
  });

  const templateData = {
    name,
    schedule: true,
    tabsOptions: tabs,
    type,
    user: userId
  };

  const { data: reportTemplateData, isLoading: isReportTemplateLoading } = useQueryFetch(
    ['reportTemplate', cacheSeed, prevScheduleConfig?.report?._id, templateData],
    async () =>
      prevScheduleConfig?.report?._id
        ? updateReportTemplate(prevScheduleConfig?.report?._id, templateData, userId, {})
        : storeNewReportTemplate(templateData, userId, {}),
    {
      enabled: isUpdating
    }
  );

  const scheduleData: Partial<ScheduleReportInterface> = {
    ...scheduleConfig,
    ...(!prevScheduleConfig?._id && {
      campaigns: campaignsIds,
      created: new Date(),
      currency: currencyOptions,
      report: reportTemplateData && reportTemplateData._id,
      user: userId
    }),
    modified: new Date(),
    name,
    timeFrame: {
      end: moment.utc(scheduleConfig.timeFrame.end).endOf('day').toDate(),
      start: moment.utc(scheduleConfig.timeFrame.start).startOf('day').toDate()
    }
  };

  const { isLoading: isScheduleReportLoading } = useQueryFetch(
    ['scheduleReport', cacheSeed, prevScheduleConfig?._id, reportTemplateData?._id],
    async () => {
      try {
        const result = await (prevScheduleConfig?._id
          ? updateScheduleReports(prevScheduleConfig!._id, scheduleData, {})
          : saveScheduleReports(scheduleData as Omit<ScheduleReportInterface, '_id'>, {}));
        showNotification(
          'success',
          formatMessage({
            defaultMessage: 'Report successfully scheduled',
            id: 'scheduleReport.validationsErrors.scheduleReportProgrammed'
          })
        );
        successCallback?.();
        setState(prevState => ({
          ...prevState,
          isUpdating: false,
          prevScheduleConfig: undefined,
          successCallback: undefined
        }));
        return result;
      } catch {
        showNotification(
          'error',
          formatMessage({
            defaultMessage: 'Report was unable to be scheduled, please try again',
            id: 'scheduleReport.validationsErrors.scheduleReportProgramError'
          })
        );
      }
    },
    {
      enabled: !isEmpty(reportTemplateData)
    }
  );

  return {
    isLoading: isReportTemplateLoading || isScheduleReportLoading,
    scheduleReport: (sCallback?: CallableFunction, prevSC?: ScheduleReportInterface) =>
      setState(prevState => ({
        ...prevState,
        cacheSeed: new Date().toISOString(),
        isUpdating: true,
        prevScheduleConfig: prevSC,
        successCallback: sCallback
      }))
  };
}
